import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, Users, ArrowRight, CheckCircle2, Star } from "lucide-react";
import Navbar from "../../components/Navbar";
import { useAuth } from "../../context/studentAuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const GroupClasses = () => {
  const { student: authStudent } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subscriptionActive, setSubscriptionActive] = useState(false);

  const student = useMemo(() => {
    if (authStudent?.id) return authStudent;
    try {
      const raw = localStorage.getItem("student");
      return raw ? JSON.parse(raw) : null;
    } catch (err) {
      return null;
    }
  }, [authStudent]);

  useEffect(() => {
    const fetchGroupClasses = async () => {
      setLoading(true);
      setError("");

      try {
        if (student?.id) {
          const subRes = await axios.get(`${BASE_URL}/api/students/subscriptions/me/${student.id}`);
          setSubscriptionActive(Boolean(subRes.data?.data?.isActive));
        } else {
          setSubscriptionActive(false);
        }

        
        const res = await axios.get(`${BASE_URL}/api/students/group-classes/approved`, {
          params: {
            studentId: student?.id || "",
          },
        });
        setClasses(res.data?.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load available group classes.");
      } finally {
        setLoading(false);
      }
    };

    fetchGroupClasses();
  }, [student?.id]);

  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString();
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 pt-28 pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <header className="mb-8">
            <h1 className="text-3xl font-extrabold text-slate-900">Available Group Classes</h1>
            <p className="text-slate-600 mt-2">
              Browse upcoming classes and open any class to view full details, watch the preview, and book your seat.
            </p>
          </header>

          {error && (
            <div className="mb-5 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600">
              Loading available classes...
            </div>
          ) : classes.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-600">
              No available group classes right now.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {classes.map((item) => {
                const booked = Number(item.max_students || 0) - Number(item.seats_left || 0);
                const isEnrolled = Number(item.is_enrolled) === 1;

                return (
                  <article
                    key={item.id}
                    className="rounded-2xl bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <h2 className="text-lg font-bold text-slate-900">{item.title}</h2>

                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {item.brand_classification === "big_brand_partner" ? (
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100">
                            <Star className="text-amber-500 fill-amber-500" size={14} />
                          </span>
                        ) : (
                          <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-200">
                            <CheckCircle2 className="text-slate-500" size={14} />
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">Tutor: {item.tutor_name || "-"}</p>

                    <div className="mt-4 space-y-2 text-sm text-slate-600">
                      <p className="flex items-center gap-2">
                        <Calendar size={15} className="text-slate-400" />
                        Start: {formatDateTime(item.start_date)}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock size={15} className="text-slate-400" />
                        End: {formatDateTime(item.end_date)}
                      </p>
                      <p className="flex items-center gap-2">
                        <Users size={15} className="text-slate-400" />
                        {booked}/{item.max_students} seats booked
                      </p>
                    </div>

                    <div className="mt-5 flex items-center justify-between gap-3">
                      <p className="text-base font-bold text-slate-900">
                        ₦{Number(item.price || 0).toLocaleString()}
                      </p>

                      <Link
                        to={`/group-classes/${item.id}`}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                      >
                        {isEnrolled ? "Open Class" : "View Class"}
                        <ArrowRight size={14} />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default GroupClasses;